/**
 * The bookkeeping behind optimistic writes: what the screen shows that the
 * server has not confirmed yet, and when it is safe to stop showing it.
 *
 * The naive version of an optimistic list — patch local state, refetch, drop
 * the patch — has a race that shows up as the exact bug it was meant to fix.
 * The refetch that follows a write can be answered from data older than the
 * write, so dropping the patch when the response lands makes the ticked task
 * reappear for a second. That flicker is why this app refused optimistic
 * updates for so long.
 *
 * The fix is to retire a local edit by *provenance*, not by timing: a response
 * to a request that left after the write settled necessarily contains it. So
 * every pending write remembers when it settled, and is only forgotten once a
 * later request has come back. Nothing here needs to know what a task or a
 * goal is, which is why it is pure and testable.
 */

/** A local edit that is already on screen, waiting for the server to agree. */
export interface PendingWrite<T> {
  /** Identity of the thing being written: a row id, or a temporary one. */
  key: string;
  /** Whatever the caller needs to render or undo the edit. */
  value: T;
  /** Epoch ms when the request succeeded; null while it is still in flight. */
  settledAt: number | null;
}

/**
 * Drop the writes a later server response has demonstrably absorbed.
 *
 * @param writes          current local edits
 * @param serverStartedAt epoch ms at which the request behind the data on
 *                        screen was *sent* (see `ApiState.startedAt`)
 *
 * In-flight writes are never retired — nothing has confirmed them yet. Returns
 * the original array when nothing changed, so callers can skip a render.
 */
export function retireSettled<T>(
  writes: PendingWrite<T>[],
  serverStartedAt: number | null,
): PendingWrite<T>[] {
  if (serverStartedAt === null) return writes;
  const kept = writes.filter((w) => w.settledAt === null || w.settledAt > serverStartedAt);
  return kept.length === writes.length ? writes : kept;
}

/**
 * Server rows plus locally-added ones that have not arrived in them yet.
 *
 * The dedupe is what keeps a freshly added row from appearing twice for a
 * frame: once the write settles, the pending copy carries the real id the
 * server assigned, so it can be recognised in the response and stood down.
 */
export function mergeAdded<T extends { id: string }>(rows: T[], added: T[]): T[] {
  if (added.length === 0) return rows;
  const known = new Set(rows.map((row) => row.id));
  return [...rows, ...added.filter((row) => !known.has(row.id))];
}

/** Server rows minus the ones a pending write has removed from the screen. */
export function withoutHidden<T extends { id: string }>(rows: T[], hidden: Set<string>): T[] {
  if (hidden.size === 0) return rows;
  return rows.filter((row) => !hidden.has(row.id));
}

/**
 * Server rows with a pending edit's version of a row swapped in, by id.
 *
 * The third shape a write can take, alongside "added" and "hidden": the row
 * stays exactly where it is and says something different. Renaming a task is
 * that, and so is moving one to today — the row is already in the list the
 * server sent, so putting the edit *back on top of it* is the whole job.
 *
 * Position is kept deliberately: a rename that reordered the day would read as
 * the list rearranging itself under the cursor.
 *
 * An id the server has not sent is ignored — a pending edit of a row nobody can
 * see is not a row — *unless* `belongs` says that edit puts the row back in
 * this list. That is the undo of a move: the task left the list, a refetch
 * absorbed the move, and now the server's answer has no row for the edit to
 * sit on. Without the append, «Повернути» would do nothing visible until the
 * next poll came back. Appended rather than inserted, because the server is the
 * one that knows where the row belongs, and it will say so in a moment.
 */
export function withPatched<T extends { id: string }>(
  rows: T[],
  patched: Map<string, T>,
  belongs?: (row: T) => boolean,
): T[] {
  if (patched.size === 0) return rows;
  let changed = false;
  const out = rows.map((row) => {
    const edit = patched.get(row.id);
    if (!edit || edit === row) return row;
    changed = true;
    return edit;
  });
  if (belongs) {
    const known = new Set(rows.map((row) => row.id));
    for (const [id, edit] of patched) {
      if (known.has(id) || !belongs(edit)) continue;
      out.push(edit);
      changed = true;
    }
  }
  return changed ? out : rows;
}
